<script setup lang="ts">
import {onMounted, ref} from "vue";
import {useNIRApplicationsStore} from "@/domains/home/services/nir/registration/stores";
import jwtService from "@/app/services/jwt.service";
import FormDataViewer from "@/domains/home/services/nir/forms/components/FormDataViewer.vue";
import {useFormsStore} from "@/domains/home/services/nir/configurations/stores/forms.ts";
import {useInstitutionStore} from "@/domains/home/services/nir/associate-registers/stores";
import AppModal from "@/components/ui/AppModal.vue";

const loading = ref(true);
const approving = ref(false);
const rejecting = ref(false);
const showApproveConfirm = ref(false);
const showRejectConfirm = ref(false);
const showFile = ref(false);
const rejectionReason = ref('');
const fileToView = ref('');
const activeTab = ref<'form' | 'attachments'>('form')
const store = useNIRApplicationsStore()
const formStore = useFormsStore()
const institutionStore = useInstitutionStore()

onMounted(() => {
  institutionStore.fetchInstitutions(1, 1000)
  fetch()
})

function fetch(){
  let id  = jwtService.getUser()["userId"]
  store.getNextTask(id)
      .then(() =>{
        loading.value = false
        formStore.fetchForm(store.task?.formId || "")
        formStore.fetchFormFields(store.task?.formId || "")
      }).
      catch(() =>{
        loading.value = false
      })
  store.fetchStatistics()
}

function approve() {
  showApproveConfirm.value = true
}

function reject() {
  rejectionReason.value = ''
  showRejectConfirm.value = true
}

function confirmApprove() {
  showApproveConfirm.value = false
  approving.value = true
  store.approve({applicationId: store.task?.id})
      .then(() =>{
        approving.value = false
        fetch()
      })
}

function confirmReject() {
  showRejectConfirm.value = false
  rejecting.value = true
  store.reject({
    applicationId: store.task?.id,
    reason: rejectionReason.value
  })
      .then(() =>{
        rejecting.value = false
        rejectionReason.value = ''
        fetch()
      })
}

function viewFile(file:string){
  showFile.value = true
  fileToView.value = file

  store.getFile({fileId: file})
}
</script>

<template>
  <div class="flex flex-col p-2">

    <div class="flex flex-col animate-pulse bg-gray-100 min-h-screen " v-if="loading"></div>
    <div class="flex flex-col" v-else>
      <div class="flex flex-col" v-if="store.task">
        <div class="flex flex-col bg-white rounded-lg shadow p-3 my-2" id="details">
          <h3 class="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
            <i class="fa fa-info-circle text-primary mr-2"></i>
            Application Details
          </h3>
          <div class="grid grid-cols-3 gap-4">
            <!-- Tracking Number -->
            <div v-if="store.task.trackingNumber" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Tracking Number</label>
              <p class="text-sm text-gray-900 font-mono">{{ store.task.trackingNumber }}</p>
            </div>

            <!-- Name -->
            <div v-if="store.task.name" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Entity Name</label>
              <p class="text-sm text-gray-900 font-semibold">{{ store.task.name }}</p>
            </div>

            <!-- Type -->
            <div v-if="store.task.type" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Type</label>
              <p class="text-sm text-gray-900">{{ store.task.type }}</p>
            </div>

            <!-- Sub Type -->
            <div v-if="store.task.subType" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Sub Type</label>
              <p class="text-sm text-gray-900">{{ store.task.subType }}</p>
            </div>

            <!-- Form ID -->
            <div v-if="store.task.formId" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Form</label>
              <p class="text-sm text-gray-900 font-mono">{{ formStore.form?.name || '' }}</p>
            </div>

            <!-- Institution ID -->
            <div v-if="store.task.institutionId" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Institution</label>
              <p class="text-sm text-gray-900 font-mono" v-if="store.task?.institutionId">{{ institutionStore.institutions?.find((i => i.id === store.task?.institutionId))?.name || 'N/A' }}</p>
            </div>

            <!-- Data File ID -->
            <div v-if="store.task.dataFileId" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Data File ID</label>
              <p class="text-sm text-gray-900 font-mono">{{ store.task.dataFileId }}</p>
            </div>

            <!-- Status -->
            <div v-if="store.task.status" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Status</label>
              <span class="text-sm font-medium px-3 py-1 rounded-full w-fit"
                    :class="{
                  'bg-gray-100 text-gray-600': store.task.status === 'PENDING',
                  'bg-gray-100 text-gray-800': store.task.status === 'SUBMITTED',
                  'bg-primary-100 text-primary-800': store.task.status === 'ASSIGNED',
                  'bg-success-100 text-success-800': store.task.status === 'APPROVED',
                  'bg-danger-100 text-danger-800': store.task.status === 'REJECTED'
                }">
            {{ store.task.status }}
          </span>
            </div>

            <!-- Created At -->
            <div v-if="store.task.createdAt" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Created At</label>
              <p class="text-sm text-gray-900">{{ new Date(store.task.createdAt).toLocaleString() }}</p>
            </div>

            <!-- Updated At -->
            <div v-if="store.task.updatedAt" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Updated At</label>
              <p class="text-sm text-gray-900">{{ new Date(store.task.updatedAt).toLocaleString() }}</p>
            </div>

            <!-- Approved At -->
            <div v-if="store.task.approvedAt" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Approved At</label>
              <p class="text-sm text-gray-900">{{ new Date(store.task.approvedAt).toLocaleString() }}</p>
            </div>

            <!-- Rejected At -->
            <div v-if="store.task.rejectedAt" class="flex flex-col">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Rejected At</label>
              <p class="text-sm text-gray-900">{{ new Date(store.task.rejectedAt).toLocaleString() }}</p>
            </div>

            <!-- Message -->
            <div v-if="store.task.message" class="flex flex-col md:col-span-2">
              <label class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</label>
              <p class="text-sm text-gray-900 bg-gray-50 p-3 rounded border border-gray-200">{{ store.task.message }}</p>
            </div>
          </div>
        </div>
        <div class="flex gap-2 justify-end pb-2" id="action-buttons">
          <button
              class="btn-primary disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:pointer-events-none"
              :disabled="!store.task"
              @click="approve">
            <i class="fa-solid fa-check-circle"></i> Approve
          </button>
          <button
              class="btn-danger disabled:opacity-40 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:pointer-events-none"
              :disabled="!store.task"
              @click="reject">
            <i class="fa-solid fa-times-circle"></i> Reject
          </button>
        </div>

        <!-- Tab Navigation -->
        <div class="flex border-b border-gray-200 mb-4">
          <label
              @click="activeTab = 'form'"
              :class="[
                'px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer',
                activeTab === 'form'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
          >
            <i class="fa fa-file-alt mr-2"></i>
            Form Data
          </label>
          <label
              @click="activeTab = 'attachments'"
              :class="[
                'px-6 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer',
                activeTab === 'attachments'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              ]"
          >
            <i class="fa fa-paperclip mr-2"></i>
            Attachments
            <span v-if="store.task?.attachments?.length" class="ml-2 bg-primary-100 text-primary-800 text-xs font-medium px-2 py-0.5 rounded-full">
              {{ store.task.attachments.length }}
            </span>
          </label>
        </div>

        <!-- Form Data Tab -->
        <div v-show="activeTab === 'form'">
          <FormDataViewer v-if="store.task && formStore.formData" :form-data="formStore.formData" :value-data="store.task.data"/>
        </div>

        <!-- Attachments Tab -->
        <div v-show="activeTab === 'attachments'">
          <div class="flex flex-col bg-white rounded-lg shadow p-6">
            <h3 class="text-xl font-semibold text-gray-900 mb-4 border-b pb-2">
              <i class="fa fa-paperclip text-primary mr-2"></i>
              Application Attachments
            </h3>

            <div v-if="store.task?.attachments?.length" class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                  v-for="attachment in store.task.attachments"
                  :key="attachment.id"
                  @click="viewFile(attachment.fileId)"
                  class="flex flex-col bg-gray-50 rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
                <div class="flex items-start justify-between">
                  <div class="flex items-center">
                    <div class="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
                      <i class="fa fa-file text-primary"></i>
                    </div>
                    <div>
                      <h4 class="text-sm font-semibold text-gray-900">{{ attachment.title }}</h4>
                      <p class="text-xs text-gray-500 mt-0.5" v-if="attachment.description">{{ attachment.description }}</p>
                    </div>
                  </div>
                </div>

                <div class="mt-3 pt-3 border-t border-gray-200">
                  <div class="flex justify-between space-y-1">
                    <div class="flex items-center text-xs text-gray-500">
                      <span class="font-medium mr-1">File ID:</span>
                      <span class="font-mono truncate" :title="attachment.fileId">{{ attachment.fileId }}</span>
                    </div>
                    <button class="btn-sm-primary" @click="viewFile(attachment.fileId)">View <i class="fas fa-eye"></i></button>
                  </div>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-12 text-gray-500">
              <i class="fa fa-folder-open text-4xl mb-3 text-gray-300"></i>
              <p class="text-gray-600">No attachments available for this application</p>
            </div>
          </div>
        </div>
      </div>
      <div v-else class="flex items-center justify-center h-200 text-gray-500">
        <div class="flex flex-col text-center items-center">
          <i class="fa fa-inbox text-4xl mb-2"></i>
          <label>No tasks available</label>
        </div>
      </div>
    </div>

    <AppModal v-model="showApproveConfirm" xl>
      <div class="p-6">
        <div class="flex items-start mb-4">
          <div class="flex-shrink-0 w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
            <i class="fa fa-check-circle text-2xl text-green-600"></i>
          </div>
          <div class="ml-4 flex-1">
            <h3 class="text-xl font-bold text-gray-900 mb-2">Approve Application</h3>
            <div class="text-sm text-gray-600 space-y-2">
              <p class="font-semibold text-gray-800">
                <i class="fa fa-exclamation-triangle text-orange-500 mr-2"></i>
                This is a critical action that cannot be reversed!
              </p>
              <p>You are about to <strong>approve</strong> this application and register the entity into the <strong>Non-Individual Register</strong>.</p>
              <p>Once approved:</p>
              <ul class="list-disc list-inside ml-4 space-y-1">
                <li>The entity will be permanently registered in the system</li>
                <li>An Entity Registration Number (ERN) will be assigned</li>
                <li>This action <strong>cannot be undone or reversed</strong></li>
                <li>The entity will become part of the official registry</li>
              </ul>
              <p class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <i class="fa fa-info-circle text-yellow-600 mr-2"></i>
                Please ensure you have thoroughly reviewed all application details before proceeding.
              </p>
            </div>
          </div>
        </div>

        <div class="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
          <button
            @click="showApproveConfirm = false"
            class="btn-danger">
            <i class="fas fa-times mr-2"></i>
            Cancel
          </button>
          <button
            @click="confirmApprove"
            class="btn-primary ">
            <i class="fa fa-check-circle mr-2"></i>
            Yes, Approve and Register

            <span class="lds-ring" v-if="approving">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </span>
          </button>
        </div>
      </div>
    </AppModal>

    <AppModal v-model="showRejectConfirm" xl>
      <div class="p-6">
        <div class="flex items-start mb-4">
          <div class="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <i class="fa fa-times-circle text-2xl text-red-600"></i>
          </div>
          <div class="ml-4 flex-1">
            <h3 class="text-xl font-bold text-gray-900 mb-2">Reject Application</h3>
            <div class="text-sm text-gray-600 space-y-2">
              <p class="font-semibold text-gray-800">
                <i class="fa fa-exclamation-triangle text-orange-500 mr-2"></i>
                This is a critical action that cannot be reversed!
              </p>
              <p>You are about to <strong>reject</strong> this application.</p>
              <p>Once rejected:</p>
              <ul class="list-disc list-inside ml-4 space-y-1">
                <li>The application will be permanently marked as rejected</li>
                <li>The entity will <strong>NOT</strong> be registered in the system</li>
                <li>This action <strong>cannot be undone or reversed</strong></li>
                <li>The applicant will be notified of the rejection</li>
              </ul>
              <p class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <i class="fa fa-info-circle text-yellow-600 mr-2"></i>
                Please ensure you have thoroughly reviewed all application details and have valid reasons for rejection.
              </p>
            </div>
          </div>
        </div>

        <!-- Rejection Reason -->
        <div class="mt-4">
          <label class="block text-sm font-semibold text-gray-700 mb-2">
            Reason for Rejection <span class="text-red-500">*</span>
          </label>
          <textarea
            v-model="rejectionReason"
            rows="4"
            placeholder="Please provide a detailed reason for rejecting this application..."
            required
            class="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 resize-none"
          ></textarea>
          <p class="text-xs text-gray-500 mt-1">
            <i class="fa fa-info-circle mr-1"></i>
            This reason will be recorded and may be shared with the applicant.
          </p>
        </div>

        <div class="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
          <button
            @click="showRejectConfirm = false"
            class="btn-danger">
            <i class="fas fa-times mr-2"></i>
            Cancel
          </button>
          <button
            @click="confirmReject"
            :disabled="!rejectionReason.trim()"
            class="btn-danger">
            <i class="fa fa-times-circle mr-2"></i>
            Yes, Reject Application

            <span class="lds-ring" v-if="rejecting">
              <div></div>
              <div></div>
              <div></div>
              <div></div>
            </span>
          </button>
        </div>
      </div>
    </AppModal>

    <AppModal v-model="showFile" xl2>
      <div class="flex flex-col items-center justify-center p-4">
        <div class="bg-white shadow-lg rounded-lg overflow-hidden" style="width: 595px; height: 842px;">
          <iframe
            :src="`data:application/pdf;base64,${store.fileBytes}#toolbar=0&navpanes=0&scrollbar=0`"
            id="pdf"
            class="w-full h-full border-0"
            style="width: 595px; height: 842px;"
          ></iframe>
        </div>
      </div>
    </AppModal>
  </div>
</template>

<style scoped>

</style>